import { useState, useEffect, useRef } from "react";

import AudioVisualiser from "./AudioVisualiser";
import { SilenceDetectedModal } from "./SilenceDetectedModal";

const AudioAnalyser = ({ audio }) => {
  const [audioData, setAudioData] = useState(new Uint8Array(0));

  const analyserRef = useRef(null);
  const rafIdRef = useRef(null);

  const [
    shouldShowSilenceDetectedModal,
    setShouldShowSilenceDetectedModal,
  ] = useState(false);

  const lastSoundTimeRef = useRef(null);
  const lastTickRef = useRef(null);
  const hasLoggedSilenceRef = useRef(false);
  const silenceThreshold = 60 * 1000; // 60 seconds until we show the modal

  useEffect(() => {
    if (!audio) return;

    // Initialize time refs when effect first runs (avoids impure performance.now() during render)
    const now = performance.now();
    if (lastSoundTimeRef.current === null)
      lastSoundTimeRef.current = now;
    if (lastTickRef.current === null) lastTickRef.current = now;

    const AudioContext =
      window.AudioContext || window.webkitAudioContext;
    const context = new AudioContext();
    const analyser = context.createAnalyser();
    const source = context.createMediaStreamSource(audio);

    analyser.fftSize = 2048;
    source.connect(analyser);

    if (context.state === "suspended") {
      context.resume();
    }

    analyserRef.current = analyser;

    const tick = () => {
      if (!analyserRef.current) return;

      const dataArray = new Uint8Array(
        analyserRef.current.frequencyBinCount,
      );
      analyserRef.current.getByteTimeDomainData(dataArray);

      const threshold = 10;
      const isCurrentlyDetectingSound = dataArray.some(
        (v) => Math.abs(v - 128) > threshold,
      );

      const currentTime = performance.now();

      if (currentTime - lastTickRef.current > 500) {
        lastSoundTimeRef.current = currentTime;
      }

      if (isCurrentlyDetectingSound) {
        hasLoggedSilenceRef.current = true;
        lastSoundTimeRef.current = currentTime;
      } else {
        const silenceDuration =
          currentTime - lastSoundTimeRef.current;

        if (
          silenceDuration > silenceThreshold &&
          !hasLoggedSilenceRef.current
        ) {
          hasLoggedSilenceRef.current = true;
          setShouldShowSilenceDetectedModal(true);
        }
      }
      lastTickRef.current = currentTime;

      setAudioData(new Uint8Array(dataArray));
      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      analyser.disconnect();
      source.disconnect();
      context.close();
    };
  }, [audio, silenceThreshold]);

  return (
    <>
      <SilenceDetectedModal
        shouldShowSilenceDetectedModal={
          shouldShowSilenceDetectedModal
        }
        setShouldShowSilenceDetectedModal={
          setShouldShowSilenceDetectedModal
        }
      />
      <div className="w-full">
        <AudioVisualiser audioData={audioData} />
      </div>
    </>
  );
};

export default AudioAnalyser;
